import React from 'react';
import PolicyLayout from './PolicyLayout';

const AccessibilityStatement = () => {
  return (
    <PolicyLayout title="Accessibility Statement" lastUpdated="January 20, 2026">
      <p>
        AppealBot is committed to ensuring digital accessibility for people with disabilities.
        We continually improve the user experience for everyone and apply relevant accessibility
        standards to ensure we provide equal access to all users.
      </p>

      <h2>Our Commitment</h2>
      <p>
        We believe that the internet should be accessible to everyone, regardless of ability.
        AppealBot strives to conform to Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
        standards. These guidelines help make web content more accessible to people with a wide
        range of disabilities, including:
      </p>
      <ul>
        <li>Visual impairments (blindness, low vision, color blindness)</li>
        <li>Hearing impairments (deafness, hard of hearing)</li>
        <li>Motor impairments (limited fine motor control)</li>
        <li>Cognitive impairments (learning disabilities, attention deficits)</li>
      </ul>

      <h2>Accessibility Features</h2>
      <p>AppealBot includes the following accessibility features:</p>

      <h3>Navigation</h3>
      <ul>
        <li>Keyboard navigation support throughout the application</li>
        <li>Skip navigation links to bypass repetitive content</li>
        <li>Consistent navigation structure across pages</li>
        <li>Descriptive page titles and headings</li>
        <li>Logical tab order for form fields and interactive elements</li>
      </ul>

      <h3>Visual Design</h3>
      <ul>
        <li>Sufficient color contrast ratios (minimum 4.5:1 for normal text)</li>
        <li>Text resizing support up to 200% without loss of functionality</li>
        <li>Information conveyed through means beyond color alone</li>
        <li>Responsive design that works across device sizes</li>
        <li>Focus indicators for keyboard navigation</li>
      </ul>

      <h3>Content</h3>
      <ul>
        <li>Alternative text for images and visual elements</li>
        <li>Descriptive link text (avoiding "click here")</li>
        <li>Clear and simple language</li>
        <li>Consistent labeling of form fields</li>
        <li>Error messages with clear instructions for correction</li>
      </ul>

      <h3>Assistive Technology Support</h3>
      <ul>
        <li>Compatible with popular screen readers (NVDA, JAWS, VoiceOver)</li>
        <li>ARIA landmarks and labels where appropriate</li>
        <li>Support for voice recognition software</li>
        <li>Compatible with screen magnification tools</li>
      </ul>

      <h2>Standards Compliance</h2>
      <p>We aim to comply with the following standards:</p>
      <ul>
        <li><strong>WCAG 2.1 Level AA:</strong> Web Content Accessibility Guidelines</li>
        <li><strong>Section 508:</strong> US federal accessibility requirements</li>
        <li><strong>ADA:</strong> Americans with Disabilities Act requirements for digital accessibility</li>
      </ul>

      <h2>Ongoing Efforts</h2>
      <p>We are continuously working to improve accessibility:</p>
      <ul>
        <li>Regular accessibility audits using automated and manual testing</li>
        <li>User testing with individuals who use assistive technologies</li>
        <li>Accessibility training for our development team</li>
        <li>Incorporating accessibility into our design and development process</li>
        <li>Monitoring and addressing accessibility issues as they are identified</li>
      </ul>

      <h2>Known Limitations</h2>
      <p>
        While we strive for full accessibility, some areas may have limitations:
      </p>
      <ul>
        <li>Some older PDF documents may not be fully accessible</li>
        <li>Third-party integrations may have varying levels of accessibility</li>
        <li>Complex data visualizations may have limited screen reader support</li>
      </ul>
      <p>
        We are actively working to address these limitations and improve accessibility
        across all areas of our platform.
      </p>

      <h2>Browser and Assistive Technology Support</h2>
      <p>AppealBot is designed to work with:</p>
      <ul>
        <li>Modern versions of Chrome, Firefox, Safari, and Edge</li>
        <li>Screen readers: NVDA, JAWS, VoiceOver, TalkBack</li>
        <li>Voice recognition: Dragon NaturallySpeaking, Windows Speech Recognition</li>
        <li>Screen magnification: ZoomText, Windows Magnifier, macOS Zoom</li>
      </ul>

      <h2>Feedback and Assistance</h2>
      <p>
        We welcome your feedback on the accessibility of AppealBot. If you encounter any
        accessibility barriers or have suggestions for improvement, please contact us:
      </p>
      <ul>
        <li>Email: accessibility@appealbot.com</li>
        <li>Phone: 1-800-APPEALBOT (request accessibility support)</li>
        <li>Response time: We aim to respond within 2 business days</li>
      </ul>

      <h3>When Contacting Us</h3>
      <p>Please include:</p>
      <ul>
        <li>Description of the accessibility issue</li>
        <li>The page or feature where you encountered the issue</li>
        <li>The assistive technology you are using (if applicable)</li>
        <li>Your preferred method of contact</li>
      </ul>

      <h2>Alternative Access</h2>
      <p>
        If you are unable to access any content or functionality on AppealBot, please
        contact us and we will work with you to provide the information or service through
        an alternative method.
      </p>

      <h2>Formal Complaints</h2>
      <p>
        If you believe AppealBot has not adequately addressed your accessibility concerns,
        you may file a formal complaint:
      </p>
      <ul>
        <li>Email: compliance@appealbot.com</li>
        <li>Subject: "Accessibility Complaint"</li>
      </ul>
      <p>
        We take all accessibility complaints seriously and will investigate and respond
        within 30 days.
      </p>

      <h2>Additional Resources</h2>
      <ul>
        <li><a href="https://www.w3.org/WAI/standards-guidelines/wcag/" target="_blank" rel="noopener noreferrer">Web Content Accessibility Guidelines (WCAG)</a></li>
        <li><a href="https://www.ada.gov/" target="_blank" rel="noopener noreferrer">Americans with Disabilities Act (ADA)</a></li>
        <li><a href="https://www.section508.gov/" target="_blank" rel="noopener noreferrer">Section 508 Standards</a></li>
      </ul>
    </PolicyLayout>
  );
};

export default AccessibilityStatement;
